import { CHAT_MAX_LINES } from '../engine/config.js';
import {
  chatFeedEl,
  chatInputEl,
  chatEmoteBtnEl,
  chatEmotePopEl,
  chatPresenceEls,
  chatChannelButtons,
  chatChannelTitleEls
} from '../ui/dom.js';
import {
  caretTextOffset,
  chatInputText,
  createEmoteNode,
  isChatInputEnabled,
  normalizeChatInput,
  selectionIsCollapsed,
  setCaretAtTextOffset,
  setChatInputText
} from './chat-input.js';
import { lobby, net, ui } from '../engine/state.js';
import { playerColorById, playerLabelById, sameHostName } from '../engine/players.js';
import { CHAT_COMMAND_NAMES, normalizeChatCommandTarget } from '../../core/chat-commands.js';

const CHAT_TEXT_LIMIT = 360;

// System lines are ranked visually by kind (see SYSTEM_KINDS in
// server/room-chat.js, which must stay in step). Whitelisted here rather than
// trusted, because the value goes on to become a class name; anything else
// falls back to the plain system style.
const CHAT_SYSTEM_KINDS = new Set(['presence', 'countdown', 'match', 'result', 'mod', 'notice']);

// Twitch-style emotes (top FrankerFaceZ set, images vendored under
// /assets/emotes): bare emote names typed or picked in chat render as inline
// images. Rendering only ever builds text nodes and <img> elements with a
// fixed src prefix, so nothing here can inject markup.
const CHAT_EMOTES = [
  'OMEGALUL',
  'Pog',
  'PepeHands',
  'widepeepoHappy',
  'LULW',
  'Pepega',
  'KEKW',
  'monkaW',
  '5Head',
  'POGGERS',
  'monkaS',
  'AYAYA',
  'PepeLaugh',
  'widepeepoSad',
  'Sadge',
  'KEKShook',
  'HYPERS',
  'YEP',
  'FeelsOkayMan',
  'KEKS',
  'EZY',
  'KEKC',
  'PepoThink',
  'gachiGASM'
];
const CHAT_EMOTE_SET = new Set(CHAT_EMOTES);

// Emote files are served with a day of caching (see cacheControlFor in
// server.js), and swapping an emote's artwork reuses its URL — so without a
// version token every client keeps the old image for a day, plus a week of
// stale-while-revalidate. The token also promotes them to immutable.
// BUMP THIS whenever an existing emote's image changes.
const EMOTE_ASSET_VERSION = 2;

function createEmoteImage(name) {
  const img = document.createElement('img');
  img.className = 'chat-emote-img';
  img.src = `/assets/emotes/${name}.png?v=${EMOTE_ASSET_VERSION}`;
  img.alt = name;
  img.title = name;
  img.loading = 'lazy';
  return img;
}

// Appends the message as text nodes with emote tokens swapped for images.
// Every emote renders at one size, whatever the message around it.
function appendRichChatText(parent, text) {
  const tokens = String(text).split(/(\s+)/);
  let pendingText = '';
  for (const token of tokens) {
    if (CHAT_EMOTE_SET.has(token)) {
      if (pendingText) {
        parent.appendChild(document.createTextNode(pendingText));
        pendingText = '';
      }
      parent.appendChild(createEmoteImage(token));
    } else {
      pendingText += token;
    }
  }
  if (pendingText) {
    parent.appendChild(document.createTextNode(pendingText));
  }
}
// The global lobby and the current room are independent channels. The shared
// feed DOM is repainted from one of these stores when its header icon is
// selected, so neither conversation is lost when the player switches views.
const LOBBY_CHAT_FEED_ID = '#lobby';
let chatRoomId = LOBBY_CHAT_FEED_ID;
let roomChatFeedId = '';
const chatFeeds = new Map();
// Personal mutes never leave this browser: hosts use the server command, while
// everyone else can hide a speaker without changing what the room receives.
const locallyMutedNames = new Map();

function createChatFeedState() {
  return {
    seenIds: new Set(),
    entries: [],
    lastPresenceSystemText: '',
    unread: false
  };
}

function chatFeedState(feedId = chatRoomId) {
  const normalized = typeof feedId === 'string' && feedId.trim()
    ? feedId.trim()
    : chatRoomId;
  if (!chatFeeds.has(normalized)) {
    chatFeeds.set(normalized, createChatFeedState());
  }
  return chatFeeds.get(normalized);
}

chatFeedState(LOBBY_CHAT_FEED_ID);

// Both counts are held, not just the visible one, so switching feeds paints the
// right number immediately instead of waiting for the next update from a socket
// that may have nothing to say for a while. null means "not known yet", which
// hides the badge rather than claiming zero.
const presenceCounts = { lobby: null, room: null };

function setLobbyPresenceCount(count) {
  presenceCounts.lobby = normalizePresenceCount(count);
  renderChatPresence();
}

function setRoomPresenceCount(count) {
  presenceCounts.room = normalizePresenceCount(count);
  renderChatPresence();
}

// Number(null) is 0, so an absent count has to be rejected before the numeric
// check or "unknown" would silently become a displayed zero.
function normalizePresenceCount(count) {
  if (count === null || count === undefined || count === '') return null;
  const value = Number(count);
  return Number.isFinite(value) && value >= 0 ? Math.floor(value) : null;
}

function renderChatPresence() {
  const inLobbyFeed = chatRoomId === LOBBY_CHAT_FEED_ID;
  const count = inLobbyFeed ? presenceCounts.lobby : presenceCounts.room;
  const known = count !== null;
  const noun = inLobbyFeed
    ? (count === 1 ? 'person in global chat' : 'people in global chat')
    : (count === 1 ? 'player in this room' : 'players in this room');
  for (const el of chatPresenceEls) {
    el.hidden = !known;
    if (!known) continue;
    const countEl = el.querySelector('.chat-presence-count');
    if (countEl) countEl.textContent = String(count);
    el.title = `${count} ${noun}`;
    el.setAttribute('aria-label', el.title);
  }
}

function channelFeedId(channel) {
  return channel === 'lobby' ? LOBBY_CHAT_FEED_ID : roomChatFeedId;
}

function renderChatChannelControls() {
  const inLobbyFeed = chatRoomId === LOBBY_CHAT_FEED_ID;
  for (const titleEl of chatChannelTitleEls) {
    titleEl.textContent = inLobbyFeed ? 'Global Chat' : 'Room Chat';
  }
  for (const button of chatChannelButtons) {
    const channel = button.dataset.chatChannel;
    const feedId = channelFeedId(channel);
    const available = channel === 'lobby' || Boolean(feedId);
    const active = available && feedId === chatRoomId;
    const unread = available && Boolean(chatFeeds.get(feedId)?.unread);
    const baseLabel = channel === 'lobby' ? 'Global lobby chat' : 'Waiting room and live game chat';
    button.disabled = !available;
    button.classList.toggle('is-active', active);
    button.classList.toggle('has-unread', unread && !active);
    button.setAttribute('aria-selected', active ? 'true' : 'false');
    button.setAttribute('aria-label', `${baseLabel}${unread && !active ? ', new messages' : ''}`);
  }
}

function renderActiveChatFeed() {
  if (!chatFeedEl) return;
  chatFeedEl.replaceChildren();
  delete chatFeedEl.dataset.empty;
  for (const entry of chatFeedState().entries) {
    if (chatEntryIsLocallyMuted(entry)) continue;
    chatFeedEl.appendChild(createChatLine(entry, chatRoomId));
  }
  ensureChatPlaceholder();
  chatFeedEl.scrollTop = chatFeedEl.scrollHeight;
}

function selectChatFeed(feedId) {
  const normalized = typeof feedId === 'string' ? feedId.trim() : '';
  if (!normalized || (normalized !== LOBBY_CHAT_FEED_ID && normalized !== roomChatFeedId)) return false;
  chatRoomId = normalized;
  chatFeedState(normalized).unread = false;
  renderActiveChatFeed();
  renderChatPresence();
  renderChatChannelControls();
  ui.controlsDirty = true;
  return true;
}

function setupChatChannels() {
  for (const button of chatChannelButtons) {
    button.addEventListener('click', () => {
      selectChatFeed(channelFeedId(button.dataset.chatChannel));
    });
  }
  renderChatChannelControls();
}

function isLobbyChatSelected() {
  return chatRoomId === LOBBY_CHAT_FEED_ID;
}

// Moderation: the server grants admin in the welcome message; deletes are
// sent through whichever channel (room/lobby) owns the feed right now.
let chatAdmin = false;
let chatDeleteHandler = null;
const lastNoticeAtByFeed = new Map();

function setChatAdmin(value) {
  const next = Boolean(value);
  if (chatAdmin === next) return;
  chatAdmin = next;
  renderActiveChatFeed();
}

function isChatAdmin() {
  return chatAdmin;
}

function setChatDeleteHandler(fn) {
  chatDeleteHandler = typeof fn === 'function' ? fn : null;
}

// Local-only system line for moderation feedback (rate limits, timeouts).
// Throttled so a spammer's own feed is not flooded with rejections.
function showChatNotice(text, feedId = chatRoomId, { force = false, kind = 'notice' } = {}) {
  const now = Date.now();
  const normalizedFeedId = typeof feedId === 'string' && feedId.trim() ? feedId.trim() : chatRoomId;
  if (!force && now - (lastNoticeAtByFeed.get(normalizedFeedId) || 0) < 1500) return;
  lastNoticeAtByFeed.set(normalizedFeedId, now);
  const systemKind = CHAT_SYSTEM_KINDS.has(kind) ? kind : '';
  appendChatEntry({ system: true, kind: systemKind, text, ts: now }, normalizedFeedId);
}

function chatBlockedNoticeText(reason, retryInMs) {
  const waitSeconds = Number.isFinite(Number(retryInMs)) && Number(retryInMs) > 0
    ? Math.ceil(Number(retryInMs) / 1000)
    : 0;
  if (reason === 'guest') {
    return 'Sign in with a passkey to chat.';
  }
  if (reason === 'timed_out') {
    if (waitSeconds > 86400) return 'You are muted.';
    return `You are timed out${waitSeconds ? ` for another ${waitSeconds}s` : ''}.`;
  }
  if (reason === 'duplicate') {
    return 'Identical message not sent.';
  }
  if (reason === 'too_fast' || reason === 'rate_limited') {
    return `You are sending messages too quickly${waitSeconds ? ` — wait ${waitSeconds}s` : ''}.`;
  }
  return 'Message not sent.';
}

function removeChatLineById(id, feedId = chatRoomId) {
  const wanted = typeof id === 'string' ? id : '';
  if (!wanted) return;
  const normalizedFeedId = typeof feedId === 'string' && feedId.trim() ? feedId.trim() : chatRoomId;
  const feed = chatFeedState(normalizedFeedId);
  const index = feed.entries.findIndex((entry) => entry.id === wanted);
  if (index !== -1) {
    feed.entries.splice(index, 1);
    feed.seenIds.delete(wanted);
  }
  if (normalizedFeedId === chatRoomId) renderActiveChatFeed();
}

// Wipe the visible messages but keep the feed bound to the same room.
function clearChatMessages(feedId = chatRoomId) {
  const normalizedFeedId = typeof feedId === 'string' && feedId.trim() ? feedId.trim() : chatRoomId;
  const feed = chatFeedState(normalizedFeedId);
  feed.seenIds.clear();
  feed.entries.length = 0;
  feed.lastPresenceSystemText = '';
  feed.unread = false;
  if (normalizedFeedId === chatRoomId) renderActiveChatFeed();
  renderChatChannelControls();
}

function padTimePart(value) {
  return String(value).padStart(2, '0');
}

function formatChatTimestamp(ts) {
  const date = new Date(Number.isFinite(Number(ts)) ? Number(ts) : Date.now());
  return [
    padTimePart(date.getHours()),
    padTimePart(date.getMinutes()),
    padTimePart(date.getSeconds())
  ].join(':');
}

function isPresenceSystemText(text) {
  return /^(?:.+) (?:joined|left) the room\.$/.test(text);
}

function ensureChatPlaceholder() {
  if (!chatFeedEl) return;
  if (chatFeedEl.children.length > 0) return;
  chatFeedEl.dataset.empty = '1';
  const placeholder = document.createElement('p');
  placeholder.className = 'chat-line system';
  placeholder.textContent = 'No messages yet.';
  chatFeedEl.appendChild(placeholder);
}

function resetChatFeed(roomId = '', force = false) {
  const normalizedRoomId = typeof roomId === 'string' ? roomId.trim() : '';
  if (!normalizedRoomId) {
    if (roomChatFeedId) chatFeeds.delete(roomChatFeedId);
    roomChatFeedId = '';
    selectChatFeed(LOBBY_CHAT_FEED_ID);
    renderChatChannelControls();
    return;
  }
  if (normalizedRoomId === LOBBY_CHAT_FEED_ID) {
    if (force) clearChatMessages(LOBBY_CHAT_FEED_ID);
    selectChatFeed(LOBBY_CHAT_FEED_ID);
    return;
  }
  if (!force && roomChatFeedId === normalizedRoomId && chatRoomId === normalizedRoomId) return;
  if (roomChatFeedId && roomChatFeedId !== normalizedRoomId) {
    chatFeeds.delete(roomChatFeedId);
  }
  roomChatFeedId = normalizedRoomId;
  if (force) clearChatMessages(normalizedRoomId);
  selectChatFeed(normalizedRoomId);
}

// A channel history can refresh without stealing focus from the other one.
// Lobby reconnects use this while a player is reading room chat.
function resetChatChannel(feedId) {
  const normalized = typeof feedId === 'string' ? feedId.trim() : '';
  if (!normalized) return;
  clearChatMessages(normalized);
}

function normalizeChatEntry(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const text = typeof raw.text === 'string' ? raw.text.trim().slice(0, CHAT_TEXT_LIMIT) : '';
  if (!text) return null;
  const id = typeof raw.id === 'string'
    ? raw.id.trim()
    : (Number.isFinite(Number(raw.id)) ? String(Math.trunc(Number(raw.id))) : '');
  const ts = Number.isFinite(Number(raw.ts)) ? Number(raw.ts) : Date.now();
  // No coercion: Number(null) is 0, which would dress lobby messages in
  // player 0's roster color.
  const playerId = Number.isFinite(raw.playerId) ? Number(raw.playerId) : null;
  const name = typeof raw.name === 'string' ? raw.name.trim().slice(0, 32) : '';
  return {
    id,
    ts,
    playerId,
    name,
    text,
    system: Boolean(raw.system),
    kind: CHAT_SYSTEM_KINDS.has(raw.kind) ? raw.kind : ''
  };
}

function isOwnChatEntry(entry) {
  return sameHostName(entry?.name, net.playerName)
    || (
      Number.isFinite(net.localPlayerId)
      && Number.isFinite(entry?.playerId)
      && Number(entry.playerId) === Number(net.localPlayerId)
    );
}

function localMuteKey(rawName) {
  return normalizeChatCommandTarget(rawName).toLowerCase();
}

function chatEntryIsLocallyMuted(entry) {
  return !entry?.system && locallyMutedNames.has(localMuteKey(entry?.name));
}

function createChatLine(entry, feedId) {
  const line = document.createElement('p');
  line.className = 'chat-line';
  if (entry.id) {
    line.dataset.chatId = entry.id;
  }

  const timestamp = formatChatTimestamp(entry.ts);
  const timeEl = document.createElement('span');
  timeEl.className = 'chat-line-time';
  timeEl.textContent = timestamp;
  line.appendChild(timeEl);

  if (entry.system) {
    line.classList.add('system');
    if (entry.kind) line.classList.add(`sys-${entry.kind}`);
    line.appendChild(document.createTextNode(entry.text));
  } else {
    const speaker = entry.name || (Number.isFinite(entry.playerId) ? playerLabelById(entry.playerId, { capitalizeYou: true }) : 'Player');
    const speakerEl = document.createElement('span');
    speakerEl.className = 'chat-line-name';
    const isSelf = isOwnChatEntry({ ...entry, name: speaker });
    if (isSelf) speakerEl.classList.add('self');
    if (Number.isFinite(entry.playerId) && entry.playerId >= 0) {
      speakerEl.style.color = playerColorById(entry.playerId);
    }
    speakerEl.textContent = `${speaker}: `;
    line.appendChild(speakerEl);
    appendRichChatText(line, entry.text);
  }

  if (chatAdmin && entry.id) {
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'chat-line-delete';
    deleteBtn.title = 'Delete message';
    deleteBtn.setAttribute('aria-label', 'Delete message');
    deleteBtn.textContent = '×';
    deleteBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      chatDeleteHandler?.(entry.id, feedId);
    });
    line.appendChild(deleteBtn);
  }

  return line;
}

function appendChatEntry(raw, feedId = chatRoomId, { notify = true } = {}) {
  const entry = normalizeChatEntry(raw);
  if (!entry) return;
  if (chatEntryIsLocallyMuted(entry)) return;
  const normalizedFeedId = typeof feedId === 'string' && feedId.trim() ? feedId.trim() : chatRoomId;
  const feed = chatFeedState(normalizedFeedId);
  if (entry.id && feed.seenIds.has(entry.id)) return;
  if (entry.id) feed.seenIds.add(entry.id);
  const isDuplicatePresence = entry.system
    && isPresenceSystemText(entry.text)
    && entry.text === feed.lastPresenceSystemText;
  if (isDuplicatePresence) return;

  feed.lastPresenceSystemText = entry.system && isPresenceSystemText(entry.text) ? entry.text : '';
  feed.entries.push(entry);
  while (feed.entries.length > CHAT_MAX_LINES) {
    const removed = feed.entries.shift();
    if (removed?.id) feed.seenIds.delete(removed.id);
  }

  if (notify && normalizedFeedId !== chatRoomId && !entry.system && !isOwnChatEntry(entry)) {
    feed.unread = true;
    renderChatChannelControls();
  }
  if (normalizedFeedId !== chatRoomId || !chatFeedEl) return;

  const shouldSnapToBottom = (chatFeedEl.scrollTop + chatFeedEl.clientHeight) >= (chatFeedEl.scrollHeight - 18);
  if (chatFeedEl.dataset.empty === '1') {
    chatFeedEl.replaceChildren();
    delete chatFeedEl.dataset.empty;
  }

  chatFeedEl.appendChild(createChatLine(entry, normalizedFeedId));
  while (chatFeedEl.children.length > CHAT_MAX_LINES) {
    const first = chatFeedEl.firstElementChild;
    if (!first) break;
    chatFeedEl.removeChild(first);
  }
  if (shouldSnapToBottom || chatFeedEl.children.length <= 2) {
    chatFeedEl.scrollTop = chatFeedEl.scrollHeight;
  }
}

function applyChatHistory(history, feedId = chatRoomId) {
  if (!Array.isArray(history)) return;
  for (const entry of history) {
    appendChatEntry(entry, feedId, { notify: false });
  }
  if (feedId === chatRoomId) ensureChatPlaceholder();
}

function buildChatLogText() {
  return chatFeedState().entries.map((entry) => {
    const timestamp = formatChatTimestamp(entry.ts);
    if (entry.system) {
      return `[${timestamp}] ${entry.text}`;
    }
    const speaker = entry.name || (Number.isFinite(entry.playerId) ? playerLabelById(entry.playerId, { capitalizeYou: true }) : 'Player');
    return `[${timestamp}] ${speaker}: ${entry.text}`;
  }).join('\n');
}

// Swaps emote tokens the player TYPED for their artwork, so a name that was
// tab-completed (or simply typed out) becomes the same thing a picked one is.
//
// Only tokens with whitespace after them are converted. Without that rule a
// name turns into a picture the instant its last letter lands, and "KEKW" could
// never be typed on the way to any longer word -- the token has to be finished
// before it is a token.
function renderChatInputEmotes() {
  if (!chatInputEl) return;
  const caret = caretTextOffset();
  let changed = false;
  for (const node of Array.from(chatInputEl.childNodes)) {
    if (node.nodeType !== 3) continue;
    const text = node.nodeValue || '';
    const pattern = /(^|\s)([A-Za-z0-9_]+)(\s)/g;
    let match;
    let cursor = 0;
    const pieces = [];
    while ((match = pattern.exec(text)) !== null) {
      if (!CHAT_EMOTE_SET.has(match[2])) continue;
      pieces.push(document.createTextNode(text.slice(cursor, match.index + match[1].length)));
      pieces.push(createEmoteNode(match[2], createEmoteImage));
      pieces.push(document.createTextNode(match[3]));
      cursor = match.index + match[0].length;
      // A trailing space consumed here cannot also open the next match, so step
      // the regex back onto it -- otherwise two adjacent emotes convert only
      // the first.
      pattern.lastIndex = cursor - match[3].length;
    }
    if (pieces.length === 0) continue;
    pieces.push(document.createTextNode(text.slice(cursor)));
    node.replaceWith(...pieces);
    changed = true;
  }
  if (changed) setCaretAtTextOffset(caret);
}

// Puts the emote's ARTWORK at the caret. It still stands for its token -- that
// is what chatInputText() reads back and what gets sent -- but the player sees
// the emote where they put it instead of the letters that name it.
function insertEmoteAtCursor(name) {
  if (!chatInputEl || !isChatInputEnabled()) return;
  chatInputEl.focus();
  const selection = window.getSelection?.();
  const before = chatInputText();
  const at = caretTextOffset();
  // Separators, for the same reason the text version had them: an emote only
  // reads as itself when it is a standalone token, and two run together would
  // serialize as one unknown word.
  const needsLeading = at > 0 && !/\s$/.test(before.slice(0, at));
  const fragment = document.createDocumentFragment();
  if (needsLeading) fragment.appendChild(document.createTextNode(' '));
  fragment.appendChild(createEmoteNode(name, createEmoteImage));
  fragment.appendChild(document.createTextNode(' '));

  if (selection && selection.rangeCount > 0 && chatInputEl.contains(selection.getRangeAt(0).endContainer)) {
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(fragment);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    chatInputEl.appendChild(fragment);
  }
  normalizeChatInput();
  setCaretAtTextOffset(at + (needsLeading ? 1 : 0) + name.length + 1);
  chatInputEl.focus();
}

// Tab completion over two name spaces at once: whoever is in the room and
// every emote. A leading @ narrows it to people (and is kept in the result);
// a bare prefix offers people first, then emotes. Repeated Tab cycles the
// matches, Shift+Tab walks back.
let chatTabCycle = null;

function knownChatNames() {
  const names = [];
  const seen = new Set();
  const push = (raw) => {
    const name = typeof raw === 'string' ? raw.trim() : '';
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(name);
  };
  for (const player of Array.isArray(lobby.players) ? lobby.players : []) push(player?.name);
  for (const spectator of Array.isArray(lobby.spectators) ? lobby.spectators : []) push(spectator?.name);
  // The roster is room-scoped, so in the lobby feed the only people we know
  // about are the ones who have said something.
  for (const entry of chatFeedState().entries) push(entry.name);
  return names;
}

function setLocalChatMuted(rawTarget, muted) {
  const requested = normalizeChatCommandTarget(rawTarget);
  const key = localMuteKey(requested);
  if (!key) return { ok: false, error: 'Choose a player to mute.' };

  if (!muted) {
    const previous = locallyMutedNames.get(key);
    if (!previous) return { ok: false, error: `${requested} is not locally muted.` };
    locallyMutedNames.delete(key);
    return { ok: true, name: previous, muted: false };
  }

  const known = knownChatNames().find((name) => localMuteKey(name) === key);
  if (!known) return { ok: false, error: `No user named "${requested}" is visible here.` };
  if (sameHostName(known, net.playerName)) {
    return { ok: false, error: 'You cannot mute yourself.' };
  }
  locallyMutedNames.set(key, known);

  // A local mute applies immediately to the conversation already on screen as
  // well as future messages. Removed entries stay removed after unmuting.
  for (const feed of chatFeeds.values()) {
    const kept = [];
    for (const entry of feed.entries) {
      if (chatEntryIsLocallyMuted(entry)) {
        if (entry.id) feed.seenIds.delete(entry.id);
      } else {
        kept.push(entry);
      }
    }
    feed.entries = kept;
  }
  renderActiveChatFeed();
  renderChatChannelControls();
  return { ok: true, name: known, muted: true };
}

function chatCompletionsFor(word) {
  if (word.startsWith('/')) {
    const prefix = word.slice(1).toLowerCase();
    return CHAT_COMMAND_NAMES
      .filter((name) => name.startsWith(prefix))
      .map((name) => `/${name}`);
  }
  const mention = word.startsWith('@');
  const prefix = (mention ? word.slice(1) : word).toLowerCase();
  if (!prefix) return [];
  const matchesPrefix = (name) => name.toLowerCase().startsWith(prefix);
  const names = knownChatNames().filter(matchesPrefix).map((name) => (mention ? `@${name}` : name));
  if (mention) return names;
  return names.concat(CHAT_EMOTES.filter(matchesPrefix));
}

function chatWordStart(value, cursor) {
  let start = cursor;
  while (start > 0 && !/\s/.test(value[start - 1])) start -= 1;
  return start;
}

function handleChatTabComplete(event) {
  if (event.key !== 'Tab' || event.ctrlKey || event.altKey || event.metaKey) return;
  if (!chatInputEl || !isChatInputEnabled()) return;
  const value = chatInputText();
  const cursor = caretTextOffset();
  // Anything but a caret sitting exactly where we left it means the field
  // moved on and the cycle is stale.
  if (!selectionIsCollapsed()) return;
  const cycling = Boolean(chatTabCycle)
    && chatTabCycle.value === value
    && chatTabCycle.cursor === cursor;

  let cycle = chatTabCycle;
  if (cycling) {
    const step = event.shiftKey ? -1 : 1;
    cycle.index = (cycle.index + step + cycle.matches.length) % cycle.matches.length;
  } else {
    const start = chatWordStart(value, cursor);
    const matches = chatCompletionsFor(value.slice(start, cursor));
    chatTabCycle = null;
    // Nothing to complete: leave Tab alone so it still moves focus.
    if (matches.length === 0) return;
    cycle = { start, matches, index: 0 };
  }

  event.preventDefault();
  event.stopPropagation();
  // Trailing space for the same reason the picker adds one: emotes only
  // render as images while they are standalone tokens.
  const inserted = `${cycle.matches[cycle.index]} `;
  const nextValue = `${value.slice(0, cycle.start)}${inserted}${value.slice(cursor)}`;
  // Rebuilt as text and re-rendered, so a completed emote name becomes its
  // image the same way a picked one does.
  setChatInputText(nextValue);
  renderChatInputEmotes();
  const nextCursor = cycle.start + inserted.length;
  setCaretAtTextOffset(nextCursor);
  chatTabCycle = { ...cycle, value: chatInputText(), cursor: nextCursor };
}

function setupChatAutocomplete() {
  if (!chatInputEl) return;
  chatInputEl.addEventListener('keydown', handleChatTabComplete);
  chatInputEl.addEventListener('input', () => {
    normalizeChatInput();
    renderChatInputEmotes();
  });
  // A contenteditable accepts pasted MARKUP by default. Take the text and
  // nothing else, so the field only ever holds what it can serialize back.
  chatInputEl.addEventListener('paste', (event) => {
    const text = event.clipboardData?.getData('text/plain');
    if (typeof text !== 'string') return;
    event.preventDefault();
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text.replace(/\r?\n/g, ' ')));
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    normalizeChatInput();
    renderChatInputEmotes();
  });
}

function setChatEmotePopVisible(visible) {
  if (!chatEmotePopEl) return;
  chatEmotePopEl.hidden = !visible;
  chatEmoteBtnEl?.classList.toggle('is-open', Boolean(visible));
}

function setupChatEmotes() {
  if (!chatEmoteBtnEl || !chatEmotePopEl) return;
  for (const name of CHAT_EMOTES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'chat-emote-option';
    btn.title = name;
    btn.setAttribute('aria-label', name);
    btn.appendChild(createEmoteImage(name));
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      insertEmoteAtCursor(name);
    });
    chatEmotePopEl.appendChild(btn);
  }
  chatEmoteBtnEl.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setChatEmotePopVisible(chatEmotePopEl.hidden);
  });
  document.addEventListener('pointerdown', (event) => {
    if (chatEmotePopEl.hidden) return;
    if (chatEmotePopEl.contains(event.target) || chatEmoteBtnEl.contains(event.target)) return;
    setChatEmotePopVisible(false);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !chatEmotePopEl.hidden) {
      setChatEmotePopVisible(false);
    }
  });
}

export {
  LOBBY_CHAT_FEED_ID,
  setLobbyPresenceCount,
  setRoomPresenceCount,
  setupChatChannels,
  setupChatEmotes,
  setupChatAutocomplete,
  resetChatFeed,
  resetChatChannel,
  isLobbyChatSelected,
  appendChatEntry,
  applyChatHistory,
  buildChatLogText,
  setChatAdmin,
  isChatAdmin,
  setChatDeleteHandler,
  showChatNotice,
  chatBlockedNoticeText,
  removeChatLineById,
  clearChatMessages,
  knownChatNames,
  setLocalChatMuted
};
